import "./axios-provider.js";
import { jsx } from "react/jsx-runtime";
import { createContext, useMemo, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOpenAppointments } from "../hooks/useOpenAppointments.js";
import { useServerSentEvent } from "../hooks/useServerSentEvent.js";
import { ServerSentResourceType } from "../open-appointments-list.types.js";
import "axios";
import "use-debounce";
const OpenAppointmentsContext = createContext({ queryKey: null, data: [], queryResult: {} });
const mapOpenAppointmentMessage = ({
  resourceId,
  clinicTreatment,
  employee,
  ...rest
}) => ({
  ...rest,
  treatmentReferenceId: clinicTreatment.referenceId,
  clinicReferenceId: resourceId.clinicReferenceId,
  treatmentName: clinicTreatment.name,
  colorCode: clinicTreatment.colorCode,
  referenceId: resourceId.referenceId,
  employee: employee ? {
    ...employee,
    displayName: employee.username
  } : null
});
const OpenAppointmentsProvider = ({
  children,
  auth,
  clinicReferenceId,
  employeeReferenceIds
}) => {
  const client = useQueryClient();
  const { data, queryKey, queryResult } = useOpenAppointments({
    employeeReferenceIds,
    clinicReferenceId: clinicReferenceId || "",
    auth: auth || ""
  });
  const handleCreateAppointment = (message) => {
    client.setQueryData(queryKey, (previousData) => {
      if (!previousData) {
        return {
          pageParams: [],
          pages: []
        };
      }
      const lastPageIndex = previousData.pages.length - 1;
      const lastPage = previousData.pages[lastPageIndex];
      if (message.resourceId.clinicReferenceId !== clinicReferenceId || !(lastPage == null ? void 0 : lastPage.last))
        return previousData;
      return {
        ...previousData,
        pages: [
          ...previousData.pages.slice(0, lastPageIndex),
          {
            ...lastPage,
            numberOfElements: lastPage.numberOfElements + 1,
            totalElements: lastPage.numberOfElements + 1,
            content: [...lastPage.content, mapOpenAppointmentMessage(message)]
          }
        ]
      };
    });
  };
  const handleUpdateAppointment = (message) => {
    client.setQueryData(queryKey, (previousData) => {
      var _a, _b;
      if (!previousData) {
        return {
          pageParams: [],
          pages: []
        };
      }
      const page = (_a = previousData == null ? void 0 : previousData.pages) == null ? void 0 : _a.findIndex(
        ({ content }) => content.some((element) => element.referenceId === message.resourceId.referenceId)
      );
      if (page < 0)
        return previousData;
      return {
        ...previousData,
        pages: [
          ...previousData.pages.slice(0, page),
          {
            ...previousData.pages[page],
            content: (_b = previousData.pages[page]) == null ? void 0 : _b.content.map(
              (element) => element.referenceId !== message.resourceId.referenceId ? element : mapOpenAppointmentMessage(message)
            )
          },
          ...previousData.pages.slice(page + 1)
        ]
      };
    });
  };
  const handleDeleteAppointment = (message) => {
    client.setQueryData(queryKey, (previousData) => {
      var _a;
      if (!previousData) {
        return {
          pageParams: [],
          pages: []
        };
      }
      const page = (_a = previousData == null ? void 0 : previousData.pages) == null ? void 0 : _a.findIndex(
        ({ content }) => content.some((element) => element.referenceId === message.resourceId.referenceId)
      );
      if (page < 0)
        return previousData;
      return {
        ...previousData,
        pages: [
          ...previousData.pages.slice(0, page),
          {
            ...previousData.pages[page],
            content: previousData.pages[page].content.filter(
              (element) => element.referenceId !== message.resourceId.referenceId
            )
          },
          ...previousData.pages.slice(page + 1)
        ]
      };
    });
  };
  useServerSentEvent(
    ServerSentResourceType.OPEN_APPOINTMENT,
    "CREATED",
    handleCreateAppointment
  );
  useServerSentEvent(
    ServerSentResourceType.OPEN_APPOINTMENT,
    "UPDATED",
    handleUpdateAppointment
  );
  useServerSentEvent(
    ServerSentResourceType.OPEN_APPOINTMENT,
    "DELETED",
    handleDeleteAppointment
  );
  const value = useMemo(
    () => ({
      data,
      queryKey,
      queryResult
    }),
    [queryKey, data, queryResult]
  );
  return /* @__PURE__ */ jsx(OpenAppointmentsContext.Provider, { value, children });
};
const useOpenAppointmentsProvider = () => {
  const context = useContext(OpenAppointmentsContext);
  if (context === void 0) {
    throw new Error("useOpenAppointmentsProvider must be used within a OpenAppointmentsProvider");
  }
  return context;
};
export {
  OpenAppointmentsProvider,
  useOpenAppointmentsProvider
};
